import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { TextBoxComponent } from '@syncfusion/ej2-react-inputs';
import { ButtonComponent, CheckBoxComponent } from '@syncfusion/ej2-react-buttons';
import { ListViewComponent } from '@syncfusion/ej2-react-lists';

export interface SelectableItem {
  selected: boolean;
  [key: string]: any;
}

export interface SelectableListProps<T extends SelectableItem> {
  items: T[];
  onSelectionChange: (items: T[]) => void;

  getItemId: (item: T) => string;
  getItemLabel: (item: T) => string;
  getItemDescription?: (item: T) => string;

  searchPlaceholder?: string;
  searchFields?: (keyof T)[];
  height?: string;

  showSelectAll?: boolean;
  showSummary?: boolean;
  showOnlySelectedFilter?: boolean;

  loading?: boolean;
  emptyMessage?: string;
  noResultsMessage?: string;

  onItemSelect?: (item: T, selected: boolean) => void;
  onFilteredItemsChange?: (filteredItems: T[]) => void;
}

interface ListRow {
  id: string;
  text: string;
  description: string;
  isChecked: boolean;
}

const listItemTemplate = (data: ListRow) => (
  <span>
    <span className="inline-block w-56 font-medium text-sm align-middle">{data.text}</span>
    {data.description && (
      <span className="text-xs text-gray-600 align-middle">{data.description}</span>
    )}
  </span>
);

export function SelectableListComponent<T extends SelectableItem>({
  items,
  onSelectionChange,
  getItemId,
  getItemLabel,
  getItemDescription,
  searchPlaceholder,
  searchFields,
  height = 'h-64',
  showSelectAll = true,
  showSummary = true,
  showOnlySelectedFilter = true,
  loading = false,
  emptyMessage,
  noResultsMessage,
  onItemSelect,
  onFilteredItemsChange,
}: SelectableListProps<T>) {
  const { t } = useTranslation(['common']);

  const [searchTerm, setSearchTerm] = useState('');
  const [showOnlySelected, setShowOnlySelected] = useState(false);

  // Apply search + "only selected" filter to the incoming items.
  const filteredItems = useMemo<T[]>(() => {
    if (!Array.isArray(items)) return [];
    let list: T[] = items;

    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      list = list.filter((item) => {
        if (searchFields && searchFields.length > 0) {
          return searchFields.some((field) => {
            const value = item[field];
            return value != null && String(value).toLowerCase().includes(lower);
          });
        }
        const label = getItemLabel(item) ?? '';
        const description = getItemDescription ? getItemDescription(item) : '';
        return (
          label.toLowerCase().includes(lower) ||
          (description ? description.toLowerCase().includes(lower) : false)
        );
      });
    }

    if (showOnlySelected) {
      list = list.filter((item) => Boolean(item.selected));
    }

    return list;
  }, [items, searchTerm, showOnlySelected, searchFields, getItemLabel, getItemDescription]);

  // Notify parent when the set of filtered items changes (compared by id).
  useEffect(() => {
    if (!onFilteredItemsChange) return;
    onFilteredItemsChange(filteredItems);
    // Intentionally only re-run when filteredItems changes — onFilteredItemsChange
    // from the parent is typically recreated on every render and would cause loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredItems]);

  // ListView dataSource: one row per visible item. Must be a new array whenever
  // visibility or selection changes so ListView re-renders with the current state.
  const dataSource = useMemo<ListRow[]>(
    () =>
      filteredItems.map((item) => ({
        id: getItemId(item),
        text: getItemLabel(item) ?? '',
        description: getItemDescription ? (getItemDescription(item) ?? '') : '',
        isChecked: Boolean(item.selected),
      })),
    [filteredItems, getItemId, getItemLabel, getItemDescription],
  );

  // Selected counter uses the full items set, not the filtered view.
  const selectedCount = useMemo(
    () => (Array.isArray(items) ? items.filter((i) => Boolean(i.selected)).length : 0),
    [items],
  );

  // Toggle a single item: called from ListView's select event.
  const setItemSelected = useCallback(
    (itemId: string, selected: boolean) => {
      const updated = items.map((item) => {
        if (getItemId(item) === itemId && Boolean(item.selected) !== selected) {
          const next = { ...item, selected } as T;
          onItemSelect?.(next, selected);
          return next;
        }
        return item;
      });
      onSelectionChange(updated);
    },
    [items, getItemId, onItemSelect, onSelectionChange],
  );

  // Bulk set: targets only currently-visible items (after search/filter).
  const setVisibleSelected = useCallback(
    (selected: boolean) => {
      const visibleIds = new Set(filteredItems.map((i) => getItemId(i)));
      if (visibleIds.size === 0) return;
      const updated = items.map((item) => {
        const id = getItemId(item);
        if (visibleIds.has(id) && Boolean(item.selected) !== selected) {
          const next = { ...item, selected } as T;
          onItemSelect?.(next, selected);
          return next;
        }
        return item;
      });
      onSelectionChange(updated);
    },
    [items, filteredItems, getItemId, onItemSelect, onSelectionChange],
  );

  const handleListSelect = useCallback(
    (args: any) => {
      // ListView's select event fires for checkbox toggle and item click.
      // args.data is the row (ListRow), args.isChecked reflects the new state.
      if (!args?.data) return;
      const row = args.data as ListRow;
      const nextChecked =
        typeof args.isChecked === 'boolean' ? args.isChecked : !row.isChecked;
      setItemSelected(row.id, nextChecked);
    },
    [setItemSelected],
  );

  // Validate required props. Hooks above must all run unconditionally.
  if (!items || !Array.isArray(items) || !getItemId || !getItemLabel || !onSelectionChange) {
    console.error('SelectableListComponent: Missing required props', {
      items: !!items,
      getItemId: !!getItemId,
      getItemLabel: !!getItemLabel,
      onSelectionChange: !!onSelectionChange,
    });
    return null;
  }

  const listHeight =
    height === '100%'
      ? 'calc(100% - 60px)'
      : height && (height.includes('%') || height.includes('px'))
      ? `calc(${height} - 60px)`
      : height || '340px';

  const hasItems = dataSource.length > 0;
  const emptyText = loading
    ? t('common.loading', 'Laden...')
    : searchTerm
    ? noResultsMessage || t('permissions.noPermissionsFound', 'Keine Ergebnisse gefunden')
    : emptyMessage || t('permissions.noPermissionsAvailable', 'Keine Elemente verfügbar');

  return (
    <div
      className="flex flex-col"
      style={{
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        maxHeight: '100%',
      }}
    >
      <div
        className="mb-3 flex gap-2 items-center flex-shrink-0"
        style={{ height: 'auto', minHeight: 'auto' }}
      >
        <div className="flex-1">
          <TextBoxComponent
            value={searchTerm}
            onChange={(e: any) => setSearchTerm(e.value || '')}
            placeholder={searchPlaceholder || t('common.search', 'Suchen...')}
            cssClass="w-full"
          />
        </div>
        {showOnlySelectedFilter && (
          <div className="flex items-center">
            <CheckBoxComponent
              checked={Boolean(showOnlySelected)}
              change={(e: { checked?: boolean }) =>
                setShowOnlySelected(Boolean(e?.checked ?? false))
              }
            />
            <label className="ml-2 text-sm font-medium text-gray-700">
              {t('permissions.showOnlySelected', 'Nur selektierte')}
            </label>
          </div>
        )}
      </div>

      <div
        className="border border-gray-300 rounded"
        style={{
          height: listHeight,
          minHeight: '200px',
          maxHeight: listHeight,
          overflowY: 'auto',
          overflowX: 'hidden',
          flex: '1 1 0%',
          flexShrink: 1,
        }}
      >
        {hasItems ? (
          <ListViewComponent
            dataSource={dataSource as unknown as { [key: string]: object }[]}
            fields={{ id: 'id', text: 'text', isChecked: 'isChecked' }}
            showCheckBox
            checkBoxPosition="Left"
            template={listItemTemplate as any}
            select={handleListSelect}
            cssClass="pb-selectable-list"
          />
        ) : (
          <div className="text-center text-gray-500 py-8">{emptyText}</div>
        )}
      </div>

      {(showSelectAll || showSummary) && (
        <div
          className="mt-3 flex justify-between items-center flex-shrink-0 border-t border-gray-200 pt-3"
          style={{ flexShrink: 0 }}
        >
          {showSelectAll && (
            <div className="flex gap-2">
              <ButtonComponent
                type="button"
                onClick={() => setVisibleSelected(true)}
                cssClass="e-outline"
              >
                {t('actions.selectAll', 'Alle auswählen')}
              </ButtonComponent>
              <ButtonComponent
                type="button"
                onClick={() => setVisibleSelected(false)}
                cssClass="e-outline"
              >
                {t('actions.deselectAll', 'Alle abwählen')}
              </ButtonComponent>
            </div>
          )}
          {showSummary && (
            <span className="text-sm text-gray-500">
              <strong>{selectedCount}</strong> von <strong>{items.length}</strong> ausgewählt
            </span>
          )}
        </div>
      )}
    </div>
  );
}
